import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Drawer, Box, Typography, Divider } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@store/index";
import {
  scorecardConfigSelector,
  scorecardConfigDispatcher,
} from "@store/slices/scorecard-config-slice";
import {
  chartDrawerSelector,
  chartDrawerDispatcher,
} from "@store/slices/chart-drawer-slice";
import {
  useGetDatasetByIdQuery,
  useGetWeightConfigurationsQuery,
} from "@store/api/dataset-api";
import { buildDefaultDateGuardrails } from "@models/Dataset";
import { getActiveMetrics } from "@models/Metric";
import type { GuardrailRollingOption, ViewerSelectableTimePeriod } from "@models/Dataset";
import type { ScorecardConfig } from "@models/Scorecard";
import { isDatasetMetricRow, isQuestionRow } from "@models/Scorecard";
import { isVisualizationBaseValid } from "@utils/visualization-base-utils";
import { getPointInTimeUnitForPeriod } from "@utils/time-period-utils";
import { getVisibleOrderedBrands } from "@utils/scorecard-column-utils";
import DrawerHeader from "./components/DrawerHeader";
import StylingSection from "./components/StylingSection";
import BrandsSection from "./components/BrandsSection";
import FormattingSection from "./components/FormattingSection";
import TimePeriodSelection from "@components/charts/TimePeriodSelection";
import RollingAverageSection from "@components/charts/RollingAverageSection";
import PointInTimeViewerOptionsSection from "@components/charts/PointInTimeViewerOptionsSection";
import RowsSection from "./components/RowsSection";
import ColumnCustomizationSection from "./components/ColumnCustomizationSection";
import DrawerFooter from "./components/DrawerFooter";
import ScorecardChart from "../ScorecardChart";

const ScorecardConfigDrawer: React.FC = () => {
  const dispatch = useAppDispatch();
  const { currentConfig, datasetId, datasetName } = useAppSelector(
    scorecardConfigSelector,
  );
  const { isDrawerOpen, editingWidgetId } = useAppSelector(chartDrawerSelector);

  const { closeChartDrawer } = chartDrawerDispatcher(dispatch);
  const {
    updateChartTitle,
    updateChartSubtitle,
    updateWeight,
    updatePercentageFormatting,
    updateMeanFormatting,
    updateTimePeriod,
    updateTimePeriodDates,
    updateRollingAverageEnabled,
    updateRollingAverageWindowSize,
    updateViewerOptions,
    setPrimaryBrand,
    setCompetitorBrandSourceIds,
    reorderBrandColumns,
    resetBrandColumnOrder,
    toggleBrandColumnVisibility,
    showAllBrandColumns,
    hideAllBrandColumns,
    updateMetricColumnHeader,
    updatePrimaryBrandHighlightColor,
    addQuestionRow,
    updateQuestionRow,
    duplicateQuestionRow,
    removeQuestionRow,
    addDatasetMetricRow,
    updateDatasetMetricRow,
    duplicateDatasetMetricRow,
    removeDatasetMetricRow,
    addHeadingRow,
    updateHeadingRow,
    removeHeadingRow,
    reorderRows,
    saveCurrentConfig,
    loadConfig,
  } = scorecardConfigDispatcher(dispatch);

  const originalConfigRef = useRef<ScorecardConfig | null>(null);
  useEffect(() => {
    if (isDrawerOpen) {
      originalConfigRef.current = currentConfig;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen, editingWidgetId]);

  const { data: weightConfigData } = useGetWeightConfigurationsQuery(
    datasetId as string,
    { skip: !datasetId },
  );
  const weightsApplied = (weightConfigData?.weightConfigurations?.length ?? 0) > 0;

  const { data: datasetDetails, refetch: refetchDataset } = useGetDatasetByIdQuery(
    datasetId as string,
    { skip: !datasetId },
  );
  const pitGuardrails = useMemo(
    () => datasetDetails?.dateGuardrails?.pointInTime ?? buildDefaultDateGuardrails().pointInTime,
    [datasetDetails?.dateGuardrails],
  );
  const activeDatasetMetrics = useMemo(
    () => getActiveMetrics(datasetDetails?.metrics ?? []),
    [datasetDetails?.metrics],
  );

  // Default a new Scorecard to DATASET_DEFAULT when the dataset has a weight configured.
  useEffect(() => {
    if (
      editingWidgetId === "new-widget" &&
      weightsApplied &&
      currentConfig.weight === "DISABLED"
    ) {
      updateWeight("DATASET_DEFAULT");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingWidgetId, weightsApplied]);

  const chartTimePeriod: ViewerSelectableTimePeriod | null =
    currentConfig.timePeriodSelection.timePeriod === "all_time" ||
    currentConfig.timePeriodSelection.timePeriod === "custom_period"
      ? null
      : currentConfig.timePeriodSelection.timePeriod;

  const currentPitUnit = useMemo(
    () => getPointInTimeUnitForPeriod(currentConfig.timePeriodSelection?.timePeriod ?? ""),
    [currentConfig.timePeriodSelection?.timePeriod],
  );
  const pitGuardrailAllowedWindowSizes = currentPitUnit
    ? pitGuardrails.units[currentPitUnit].allowedRollingAverages.filter((n) => n !== 0)
    : undefined;

  const hasRow = currentConfig.rows.some(
    (row) => isQuestionRow(row) || isDatasetMetricRow(row),
  );
  const hasUnresolvedMapping = currentConfig.rows.some(
    (row) =>
      (isQuestionRow(row) || isDatasetMetricRow(row)) &&
      row.brandMappings.some((m) => m.status === "unresolved"),
  );
  // Also blocks Save on an incomplete Custom/Pre-configured Base, a Question
  // row whose Scorecard Row Base was force-cleared by deleting its source
  // row, or a New Question Base with its own unresolved brand mapping.
  const hasInvalidRowBase = currentConfig.rows.some(
    (row) =>
      isQuestionRow(row) &&
      (!isVisualizationBaseValid(row.visualizationBase) ||
        row.rowBaseNeedsAttention ||
        (row.rowBase?.kind === "new_question_base" &&
          row.rowBase.questionBase.brandMappings.some((m) => m.status === "unresolved"))),
  );
  const hasNoVisibleBrandColumns =
    getVisibleOrderedBrands(
      currentConfig.brands,
      currentConfig.columnOrder,
      currentConfig.hiddenBrandIds,
    ).length === 0;
  const canSave =
    !!currentConfig.sourceQuestion &&
    hasRow &&
    !hasUnresolvedMapping &&
    !hasInvalidRowBase &&
    !hasNoVisibleBrandColumns;

  const [isValidatingSave, setIsValidatingSave] = useState(false);
  const [hasStaleMetricReference, setHasStaleMetricReference] = useState(false);

  // Concurrent-deletion guard: a Metric row's referenced DatasetMetric could
  // have been deleted by someone else since this drawer's own (cached)
  // dataset query last resolved. Re-verify against a forced fresh fetch
  // right before persisting rather than trusting the cache. Reuses the
  // existing "Unavailable metric" indicator in RowListItem to surface the
  // affected row(s) — refetching updates activeDatasetMetrics, which that
  // indicator already reads.
  const handleSave = async () => {
    const metricRows = currentConfig.rows.filter(isDatasetMetricRow);
    if (metricRows.length > 0) {
      setIsValidatingSave(true);
      try {
        const fresh = await refetchDataset().unwrap();
        const freshActiveIds = new Set(getActiveMetrics(fresh.metrics ?? []).map((m) => m.id));
        const hasDangling = metricRows.some((row) => !freshActiveIds.has(row.datasetMetricId));
        if (hasDangling) {
          setHasStaleMetricReference(true);
          return;
        }
      } catch {
        // Fetch itself failed — fall through and let the normal save attempt
        // (and its own error handling) surface the problem.
      } finally {
        setIsValidatingSave(false);
      }
    }
    setHasStaleMetricReference(false);
    saveCurrentConfig();
    setTimeout(() => {
      closeChartDrawer();
    }, 50);
  };

  const handleDiscard = () => {
    if (originalConfigRef.current) loadConfig(originalConfigRef.current);
    closeChartDrawer();
  };

  return (
    <Drawer
      anchor="right"
      open={isDrawerOpen}
      onClose={handleDiscard}
      sx={{ "& .MuiDrawer-paper": { width: "80vw", boxSizing: "border-box" } }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <DrawerHeader
          title={currentConfig.title}
          subtitle={currentConfig.subtitle}
          datasetName={datasetName}
        />

        <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* Configuration Panel */}
          <Box
            sx={{
              width: 400,
              borderRight: 1,
              borderColor: "divider",
              overflow: "auto",
              p: 3,
            }}
          >
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
              Configurations
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <BrandsSection
                sourceQuestion={currentConfig.sourceQuestion}
                brandCandidates={currentConfig.brandCandidates}
                brands={currentConfig.brands}
                onSetPrimary={setPrimaryBrand}
                onSetCompetitors={setCompetitorBrandSourceIds}
              />

              <Divider />

              <RowsSection
                rows={currentConfig.rows}
                brands={currentConfig.brands}
                datasetId={datasetId}
                datasetMetrics={activeDatasetMetrics}
                onAddQuestionRow={addQuestionRow}
                onUpdateQuestionRow={(id, row) => updateQuestionRow({ id, changes: row })}
                onDuplicateQuestionRow={duplicateQuestionRow}
                onRemoveQuestionRow={removeQuestionRow}
                onAddDatasetMetricRow={addDatasetMetricRow}
                onUpdateDatasetMetricRow={(id, row) => updateDatasetMetricRow({ id, changes: row })}
                onDuplicateDatasetMetricRow={duplicateDatasetMetricRow}
                onRemoveDatasetMetricRow={removeDatasetMetricRow}
                onAddHeadingRow={addHeadingRow}
                onUpdateHeadingRow={(id, label) => updateHeadingRow({ id, label })}
                onRemoveHeadingRow={removeHeadingRow}
                onReorderRows={reorderRows}
              />

              <Divider />

              <FormattingSection
                weight={currentConfig.weight}
                weightsApplied={weightsApplied}
                percentageFormatting={currentConfig.percentageFormatting}
                meanFormatting={currentConfig.meanFormatting}
                onWeightChange={updateWeight}
                onPercentageFormattingChange={updatePercentageFormatting}
                onMeanFormattingChange={updateMeanFormatting}
              />

              <Divider />

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Time Period
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TimePeriodSelection
                    timePeriodSelection={currentConfig.timePeriodSelection}
                    onTimePeriodChange={updateTimePeriod}
                    onTimePeriodDatesChange={updateTimePeriodDates}
                    responsesMinDate={currentConfig.responsesMinDate}
                    responsesMaxDate={currentConfig.responsesMaxDate}
                  />

                  <RollingAverageSection
                    enabled={currentConfig.rollingAverage.enabled}
                    windowSize={currentConfig.rollingAverage.windowSize}
                    timePeriod={currentConfig.timePeriodSelection.timePeriod}
                    onEnabledChange={updateRollingAverageEnabled}
                    onWindowSizeChange={(size) =>
                      updateRollingAverageWindowSize(size as GuardrailRollingOption)
                    }
                    guardrailAllowedWindowSizes={pitGuardrailAllowedWindowSizes}
                  />
                </Box>
              </Box>

              <PointInTimeViewerOptionsSection
                viewerOptions={currentConfig.viewerOptions}
                chartTimePeriod={chartTimePeriod}
                chartRollingWindowSize={
                  currentConfig.rollingAverage.enabled
                    ? currentConfig.rollingAverage.windowSize
                    : 0
                }
                pitGuardrailUnits={pitGuardrails.units}
                isDisabled={false}
                onViewerOptionsChange={updateViewerOptions}
                datasetId={datasetId}
              />

              <Divider />

              <StylingSection
                title={currentConfig.title}
                subtitle={currentConfig.subtitle}
                metricColumnHeader={currentConfig.metricColumnHeader}
                primaryBrandLabel={
                  currentConfig.brands.find((b) => b.role === "primary")?.label ?? null
                }
                primaryBrandHighlightColor={currentConfig.primaryBrandHighlightColor}
                onTitleChange={updateChartTitle}
                onSubtitleChange={updateChartSubtitle}
                onMetricColumnHeaderChange={updateMetricColumnHeader}
                onPrimaryBrandHighlightColorChange={updatePrimaryBrandHighlightColor}
              />

              <Divider />

              <ColumnCustomizationSection
                brands={currentConfig.brands}
                columnOrder={currentConfig.columnOrder}
                hiddenBrandIds={currentConfig.hiddenBrandIds}
                onToggleVisibility={toggleBrandColumnVisibility}
                onShowAll={showAllBrandColumns}
                onHideAll={hideAllBrandColumns}
                onReorder={(fromIndex, toIndex) =>
                  reorderBrandColumns({ fromIndex, toIndex })
                }
                onResetOrder={resetBrandColumnOrder}
              />
            </Box>
          </Box>

          {/* Preview Panel */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              p: 3,
              backgroundColor: "#ffffff",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Preview
            </Typography>
            <Box
              sx={{
                flex: 1,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                minHeight: 400,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "auto",
              }}
            >
              <ScorecardChart config={currentConfig} datasetId={datasetId ?? undefined} />
            </Box>
          </Box>
        </Box>

        {hasStaleMetricReference && (
          <Alert severity="warning" sx={{ mx: 3, mb: 1 }}>
            One or more Metric rows reference a Dataset Metric that's no longer available.
            Reopen the row marked "Unavailable metric" in the Rows list to replace it before
            saving.
          </Alert>
        )}

        {hasNoVisibleBrandColumns && (
          <Alert severity="warning" sx={{ mx: 3, mb: 1 }}>
            No brand columns are visible. Show at least one brand in Column Customization
            before updating.
          </Alert>
        )}

        <DrawerFooter
          canSave={canSave && !isValidatingSave}
          isCreatingChart={editingWidgetId === "new-widget"}
          onSave={handleSave}
          onDiscard={handleDiscard}
        />
      </Box>
    </Drawer>
  );
};

export default ScorecardConfigDrawer;
